import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import config from "../../config";
import LoginChecked from "../Shared/LoginChecked";
import {
  Table,
  DatePicker,
  Form,
  Row,
  Col,
  Alert,
  Tag,
  Button,
  Typography,
  Space,
  Checkbox,
  Descriptions,
} from "antd";
import { NavLink } from "react-router-dom";
import dayjs from "dayjs";
import { NumberFormat } from "../Shared/Utility";
import {
  WarningOutlined,
  ReloadOutlined,
  LinkOutlined,
} from "@ant-design/icons";

const { Paragraph, Text } = Typography;

function documentLink(refType, refNo) {
  const t = (refType || "").trim().toLowerCase();
  const fixQs = "posting_fix=1";
  if (t === "sale" || t === "sales") {
    return `/sales/edit-sales/${refNo}?${fixQs}`;
  }
  if (t === "purchase") {
    return `/purchase/edit-purchase/${refNo}?${fixQs}`;
  }
  if (t === "voucher") {
    return `/vouchers/manage-vouchers?voucher_id=${refNo}&${fixQs}`;
  }
  return null;
}

function PostingIntegrity() {
  const { RangePicker } = DatePicker;
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");
  const [loading, setLoading] = useState(false);
  const [StartDate, setStartDate] = useState(
    dayjs().subtract(90, "day").format("YYYY-MM-DD")
  );
  const [EndDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [rows, setRows] = useState([]);
  const [imbalanceCount, setImbalanceCount] = useState(0);
  const [totalAbsDifference, setTotalAbsDifference] = useState(0);
  /** When false, per-document scan uses all transaction dates (recommended). */
  const [restrictDates, setRestrictDates] = useState(false);
  const [recon, setRecon] = useState(null);
  const [accountDrifts, setAccountDrifts] = useState([]);
  const [totalSignedDriftSum, setTotalSignedDriftSum] = useState(null);
  /** Same Reference_No used with more than one normalized Reference_Type (e.g. Voucher + Sale). */
  const [referenceNoTypeCollisions, setReferenceNoTypeCollisions] = useState([]);
  const [ledgerLinesBothDebitAndCredit, setLedgerLinesBothDebitAndCredit] = useState(0);
  /** Same as-of date as Manage Accounts / GetAcountBalanceOnDate (default today on server). */
  const [balanceAsOfPicker, setBalanceAsOfPicker] = useState(() =>
    dayjs().format("YYYY-MM-DD")
  );

  const rangeValue = useMemo(() => {
    const a = dayjs(StartDate, "YYYY-MM-DD", true);
    const b = dayjs(EndDate, "YYYY-MM-DD", true);
    if (!a.isValid() || !b.isValid()) return null;
    return [a, b];
  }, [StartDate, EndDate]);

  const load = () => {
    setLoading(true);
    const data = {
      UserID,
      AccessKey,
    };
    if (balanceAsOfPicker) {
      const d = dayjs(balanceAsOfPicker, "YYYY-MM-DD", true);
      if (d.isValid()) data.Dated = d.format("YYYY-MM-DD");
    }
    if (restrictDates) {
      data.StartDate = StartDate;
      data.EndDate = EndDate;
    }
    axios({
      method: "post",
      url: config.base_url + "account/GetLedgerPostingImbalances",
      headers: { "Content-Type": "application/json" },
      data,
    })
      .then((res) => {
        if (res.data.status_code === 1) {
          const list = res.data.imbalances ?? res.data.Imbalances ?? [];
          setRows(Array.isArray(list) ? list : []);
          setImbalanceCount(
            res.data.imbalanceCount ?? res.data.ImbalanceCount ?? list.length
          );
          const tad =
            res.data.totalAbsDifference ?? res.data.TotalAbsDifference ?? 0;
          setTotalAbsDifference(Number(tad) || 0);
          setRecon({
            ledgerLineDebitTotal:
              res.data.ledgerLineDebitTotal ?? res.data.LedgerLineDebitTotal,
            ledgerLineCreditTotal:
              res.data.ledgerLineCreditTotal ?? res.data.LedgerLineCreditTotal,
            ledgerLineDifference:
              res.data.ledgerLineDifference ?? res.data.LedgerLineDifference,
            ledgerLineDebitTotalAllTime:
              res.data.ledgerLineDebitTotalAllTime ??
              res.data.LedgerLineDebitTotalAllTime,
            ledgerLineCreditTotalAllTime:
              res.data.ledgerLineCreditTotalAllTime ??
              res.data.LedgerLineCreditTotalAllTime,
            ledgerLineDifferenceAllTime:
              res.data.ledgerLineDifferenceAllTime ??
              res.data.LedgerLineDifferenceAllTime,
            accountBalanceDebitTotal:
              res.data.accountBalanceDebitTotal ??
              res.data.AccountBalanceDebitTotal,
            accountBalanceCreditTotal:
              res.data.accountBalanceCreditTotal ??
              res.data.AccountBalanceCreditTotal,
            accountBalanceDifference:
              res.data.accountBalanceDifference ??
              res.data.AccountBalanceDifference,
            balanceAsOfDate:
              res.data.balanceAsOfDate ?? res.data.BalanceAsOfDate,
            ledgerLineDebitTotalAsOf:
              res.data.ledgerLineDebitTotalAsOf ??
              res.data.LedgerLineDebitTotalAsOf,
            ledgerLineCreditTotalAsOf:
              res.data.ledgerLineCreditTotalAsOf ??
              res.data.LedgerLineCreditTotalAsOf,
            ledgerLineDifferenceAsOf:
              res.data.ledgerLineDifferenceAsOf ??
              res.data.LedgerLineDifferenceAsOf,
          });
          const drifts =
            res.data.accountDrifts ?? res.data.AccountDrifts ?? [];
          setAccountDrifts(Array.isArray(drifts) ? drifts : []);
          const tsd =
            res.data.totalSignedDriftSum ?? res.data.TotalSignedDriftSum;
          setTotalSignedDriftSum(tsd != null ? Number(tsd) : null);
          const coll =
            res.data.referenceNoTypeCollisions ??
            res.data.ReferenceNoTypeCollisions ??
            [];
          setReferenceNoTypeCollisions(Array.isArray(coll) ? coll : []);
          const bothDc =
            res.data.ledgerLinesBothDebitAndCredit ??
            res.data.LedgerLinesBothDebitAndCredit ??
            0;
          setLedgerLinesBothDebitAndCredit(Number(bothDc) || 0);
        } else {
          setRows([]);
          setImbalanceCount(0);
          setTotalAbsDifference(0);
          setRecon(null);
          setAccountDrifts([]);
          setTotalSignedDriftSum(null);
          setReferenceNoTypeCollisions([]);
          setLedgerLinesBothDebitAndCredit(0);
        }
        setLoading(false);
      })
      .catch(() => {
        setRows([]);
        setRecon(null);
        setAccountDrifts([]);
        setTotalSignedDriftSum(null);
        setReferenceNoTypeCollisions([]);
        setLedgerLinesBothDebitAndCredit(0);
        setLoading(false);
      });
  };

  useEffect(() => {
    document.title = "Posting diagnostics";
    load();
    // Intentionally run once on mount; user refreshes via "Run check" after changing dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      setStartDate(dates[0].format("YYYY-MM-DD"));
      setEndDate(dates[1].format("YYYY-MM-DD"));
    }
  };

  const columns = [
    {
      title: "Source",
      dataIndex: "Reference_Type",
      key: "Reference_Type",
      render: (v) => {
        const s = (v || "").trim() || "(blank)";
        const color =
          s.toLowerCase() === "voucher"
            ? "blue"
            : s.toLowerCase() === "sale" || s.toLowerCase() === "sales"
            ? "green"
            : s.toLowerCase() === "purchase"
            ? "orange"
            : "default";
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: "Ref. no.",
      dataIndex: "Reference_No",
      key: "Reference_No",
      align: "right",
    },
    {
      title: "Debit total",
      dataIndex: "TotalDebit",
      key: "TotalDebit",
      align: "right",
      render: (v) => NumberFormat(v, 2),
    },
    {
      title: "Credit total",
      dataIndex: "TotalCredit",
      key: "TotalCredit",
      align: "right",
      render: (v) => NumberFormat(v, 2),
    },
    {
      title: "Difference (Dr − Cr)",
      dataIndex: "Difference",
      key: "Difference",
      align: "right",
      render: (v) => NumberFormat(v, 2),
    },
    {
      title: "Lines",
      dataIndex: "LineCount",
      key: "LineCount",
      align: "right",
    },
    {
      title: "Dr lines",
      key: "DebitLineCount",
      align: "right",
      render: (_, r) =>
        r.DebitLineCount ?? r.debitLineCount ?? "—",
    },
    {
      title: "Cr lines",
      key: "CreditLineCount",
      align: "right",
      render: (_, r) =>
        r.CreditLineCount ?? r.creditLineCount ?? "—",
    },
    {
      title: "Sides",
      key: "sides",
      width: 110,
      render: (_, r) => {
        const dl = Number(r.DebitLineCount ?? r.debitLineCount ?? 0);
        const cl = Number(r.CreditLineCount ?? r.creditLineCount ?? 0);
        if (dl > 0 && cl === 0) {
          return <Tag color="red">Debit-only lines</Tag>;
        }
        if (cl > 0 && dl === 0) {
          return <Tag color="red">Credit-only lines</Tag>;
        }
        return <Tag color="default">Dr + Cr</Tag>;
      },
    },
    {
      title: "First transaction",
      dataIndex: "FirstTransactionDate",
      key: "FirstTransactionDate",
      render: (d) =>
        d
          ? dayjs(d).format(
              config.date_format ? config.date_format : "DD/MM/YYYY"
            )
          : "—",
    },
    {
      title: "Open",
      key: "open",
      render: (_, record) => {
        const href = documentLink(
          record.Reference_Type ?? record.reference_Type,
          record.Reference_No ?? record.reference_No
        );
        if (!href) {
          return <Text type="secondary">—</Text>;
        }
        return (
          <NavLink to={href} target="_blank" rel="noreferrer">
            <LinkOutlined /> Open
          </NavLink>
        );
      },
    },
  ];

  const driftColumns = [
    {
      title: "Account ID",
      key: "aid",
      align: "right",
      width: 110,
      render: (_, r) => {
        const id = r.Account_ID ?? r.account_ID;
        return (
          <NavLink to={`/accounts/account-ledger/?account_id=${id}`}>
            {id}
          </NavLink>
        );
      },
    },
    {
      title: "Name",
      key: "name",
      ellipsis: true,
      render: (_, r) => r.Name ?? r.name ?? "—",
    },
    {
      title: "Stored (Dr/Cr)",
      key: "bt",
      width: 100,
      render: (_, r) => r.StoredBalanceType ?? r.storedBalanceType ?? "—",
    },
    {
      title: "Stored balance",
      key: "sb",
      align: "right",
      render: (_, r) =>
        NumberFormat(r.StoredBalance ?? r.storedBalance ?? 0, 2),
    },
    {
      title: "GL Σ Debit",
      key: "sd",
      align: "right",
      render: (_, r) => NumberFormat(r.SumDebit ?? r.sumDebit ?? 0, 2),
    },
    {
      title: "GL Σ Credit",
      key: "sc",
      align: "right",
      render: (_, r) => NumberFormat(r.SumCredit ?? r.sumCredit ?? 0, 2),
    },
    {
      title: "GL net (Dr − Cr)",
      key: "lsn",
      align: "right",
      render: (_, r) =>
        NumberFormat(r.LedgerSignedNet ?? r.ledgerSignedNet ?? 0, 2),
    },
    {
      title: "Drift",
      key: "drift",
      align: "right",
      render: (_, r) => {
        const d = Number(r.Drift ?? r.drift ?? 0);
        return (
          <Text strong={Math.abs(d) > 0.01} type={Math.abs(d) > 0.01 ? "danger" : undefined}>
            {NumberFormat(d, 2)}
          </Text>
        );
      },
    },
  ];

  const glAsOfOk =
    !recon ||
    recon.ledgerLineDifferenceAsOf == null ||
    Math.abs(Number(recon.ledgerLineDifferenceAsOf) || 0) <= 0.01;
  const showAccountOnlyMismatch =
    recon &&
    Math.abs(Number(recon.accountBalanceDifference) || 0) > 0.01 &&
    glAsOfOk &&
    Math.abs(Number(recon.ledgerLineDifferenceAllTime) || 0) <= 0.01 &&
    imbalanceCount === 0;

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Posting diagnostics</h3>
            <div className="header-actions">
              <NavLink to="/accounts/manage-accounts">Manage accounts</NavLink>
            </div>
          </div>

          <Paragraph type="secondary" style={{ maxWidth: 920 }}>
            <Text strong>Manage Accounts</Text> and the <Text strong>account / drift</Text> section below
            use the same <Text strong>as-of date</Text> (default today) and the same GL rules as{" "}
            <Text code>GetAcountBalanceOnDate</Text>. Per-account drift compares those account totals to
            Σ Debit − Σ Credit in <Text code>tblGeneralLedger</Text> through that date — not all-time
            versus as-of, which previously made totals impossible to reconcile. The table at the bottom
            finds <Text strong>per-document</Text> posting issues (unequal debits/credits per
            voucher/sale/purchase group). Groups use <Text strong>normalized</Text>{" "}
            <Text code>Reference_Type</Text> (trim + case-insensitive) so &quot;Sale&quot; and
            &quot;Sales&quot; do not split one transaction into two fake &quot;balanced&quot; groups.
            Use <Text strong>Open</Text> to edit the document; links from this page include a
            fix key so the usual edit day-limit is bypassed when you re-save.
          </Paragraph>

          {recon && (
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, sm: 1, md: 2 }}
              title={
                recon.balanceAsOfDate
                  ? `Reconciliation (same pump — balances through ${dayjs(recon.balanceAsOfDate).format(
                      config.date_format ? config.date_format : "DD/MM/YYYY"
                    )})`
                  : "Reconciliation (same pump)"
              }
              style={{ marginBottom: 16, maxWidth: 960 }}
            >
              <Descriptions.Item label="Account balances (Manage Accounts) — Dr sum">
                Rs. {NumberFormat(recon.accountBalanceDebitTotal, 2)}
              </Descriptions.Item>
              <Descriptions.Item label="Account balances — Cr sum">
                Rs. {NumberFormat(recon.accountBalanceCreditTotal, 2)}
              </Descriptions.Item>
              <Descriptions.Item label="Difference (Dr − Cr)" span={2}>
                Rs.{" "}
                {NumberFormat(recon.accountBalanceDifference, 2)}
                {Math.abs(Number(recon.accountBalanceDifference) || 0) > 0.01 ? (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    Mismatch on Manage Accounts
                  </Tag>
                ) : (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    OK
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="GL lines — same as-of date (Σ Dr)">
                Rs.{" "}
                {NumberFormat(
                  recon.ledgerLineDebitTotalAsOf != null
                    ? recon.ledgerLineDebitTotalAsOf
                    : 0,
                  2
                )}
              </Descriptions.Item>
              <Descriptions.Item label="GL lines — same as-of date (Σ Cr)">
                Rs.{" "}
                {NumberFormat(
                  recon.ledgerLineCreditTotalAsOf != null
                    ? recon.ledgerLineCreditTotalAsOf
                    : 0,
                  2
                )}
              </Descriptions.Item>
              <Descriptions.Item label="GL same as-of (Dr − Cr)" span={2}>
                Rs.{" "}
                {NumberFormat(
                  recon.ledgerLineDifferenceAsOf != null
                    ? recon.ledgerLineDifferenceAsOf
                    : 0,
                  2
                )}
                {Math.abs(Number(recon.ledgerLineDifferenceAsOf) || 0) > 0.01 ? (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    GL does not balance through this date
                  </Tag>
                ) : (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Debits = credits (through this date)
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="GL lines — sum(Debit) all time">
                Rs. {NumberFormat(recon.ledgerLineDebitTotalAllTime, 2)}
              </Descriptions.Item>
              <Descriptions.Item label="GL lines — sum(Credit) all time">
                Rs. {NumberFormat(recon.ledgerLineCreditTotalAllTime, 2)}
              </Descriptions.Item>
              <Descriptions.Item label="GL all-time (Dr − Cr)" span={2}>
                Rs. {NumberFormat(recon.ledgerLineDifferenceAllTime, 2)}
                {Math.abs(Number(recon.ledgerLineDifferenceAllTime) || 0) > 0.01 ? (
                  <Tag color="red" style={{ marginLeft: 8 }}>
                    Ledger lines do not balance
                  </Tag>
                ) : (
                  <Tag color="green" style={{ marginLeft: 8 }}>
                    Debits = credits in GL
                  </Tag>
                )}
              </Descriptions.Item>
              {restrictDates ? (
                <>
                  <Descriptions.Item label="GL lines in selected range — Dr">
                    Rs. {NumberFormat(recon.ledgerLineDebitTotal, 2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="GL lines in range — Cr">
                    Rs. {NumberFormat(recon.ledgerLineCreditTotal, 2)}
                  </Descriptions.Item>
                  <Descriptions.Item label="GL in range (Dr − Cr)" span={2}>
                    Rs. {NumberFormat(recon.ledgerLineDifference, 2)}
                  </Descriptions.Item>
                </>
              ) : null}
            </Descriptions>
          )}

          {recon && !loading && (
            <div style={{ marginBottom: 16, maxWidth: 1100 }}>
              <Text strong>Account balance vs ledger (per account)</Text>
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 8 }}>
                For each account, <Text strong>Drift</Text> = balance from{" "}
                <Text code>GetAcountBalanceOnDate</Text> (signed) minus Σ Debit − Σ Credit in{" "}
                <Text code>tblGeneralLedger</Text> through the same as-of date. If the stored procedure
                matches this query, drift should be zero and trial difference should be zero. Rows show
                accounts where they still disagree (fix SP or data). The sum of drift across all
                accounts equals &quot;Difference (Dr − Cr)&quot; above.
              </Paragraph>
              {totalSignedDriftSum != null && !Number.isNaN(totalSignedDriftSum) ? (
                <Paragraph type="secondary" style={{ marginBottom: 8 }}>
                  Sum of drift (all accounts): Rs. {NumberFormat(totalSignedDriftSum, 2)} — should
                  match &quot;Difference (Dr − Cr)&quot; in Reconciliation above.
                </Paragraph>
              ) : null}
              <Table
                size="small"
                bordered
                rowKey={(r, i) => `drift-${r.Account_ID ?? r.account_ID ?? i}`}
                columns={driftColumns}
                dataSource={accountDrifts}
                pagination={{ pageSize: 15, showSizeChanger: true }}
                scroll={{ x: true }}
                locale={{
                  emptyText:
                    Math.abs(Number(recon.accountBalanceDifference) || 0) <= 0.01
                      ? "No accounts with stored vs ledger mismatch."
                      : "No per-account drift rows (unexpected if Manage Accounts shows a mismatch — refresh or contact support).",
                }}
              />
            </div>
          )}

          {!loading && referenceNoTypeCollisions.length > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, maxWidth: 960 }}
              message="Same reference number used with different document types"
              description={
                <>
                  These <Text code>Reference_No</Text> values appear under more than one normalized{" "}
                  <Text code>Reference_Type</Text> in the scanned period (e.g. Voucher #5 and Sale #5).
                  Open the account ledger filtered by amount/date, or query{" "}
                  <Text code>tblGeneralLedger</Text> for each number. Ref. nos:{" "}
                  {referenceNoTypeCollisions
                    .slice(0, 40)
                    .map((c) => c.referenceNo ?? c.ReferenceNo)
                    .join(", ")}
                  {referenceNoTypeCollisions.length > 40
                    ? ` … (+${referenceNoTypeCollisions.length - 40} more)`
                    : ""}
                </>
              }
            />
          ) : null}

          {!loading && ledgerLinesBothDebitAndCredit > 0 ? (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16, maxWidth: 960 }}
              message={`${ledgerLinesBothDebitAndCredit} ledger line(s) have both Debit and Credit non-zero`}
              description="Normal lines usually post only one side. Review these rows in the database or a raw GL export; they can skew account-level reports."
            />
          ) : null}

          {showAccountOnlyMismatch && (
              <Alert
                type="info"
                showIcon
                style={{ marginBottom: 16, maxWidth: 960 }}
                message="No per-document posting errors found"
                description={
                  <>
                    Stored account balances (Manage Accounts) do not match each other, but the general
                    ledger lines balance globally and each source document group looks balanced. This
                    usually means account balances need to be rebuilt from the ledger (or a legacy
                    data issue), not a missing voucher/sale/purchase leg. Use the table{" "}
                    <Text strong>Account balance vs ledger</Text> above to see which account IDs are
                    wrong, then open each in the account ledger. Consult your DBA or support if you
                    need a bulk rebalance from <Text code>tblGeneralLedger</Text>.
                  </>
                }
              />
            )}

          {imbalanceCount === 0 && !loading && !showAccountOnlyMismatch ? (
            <Alert
              type="success"
              showIcon
              message={
                restrictDates
                  ? "No imbalanced posting groups in the selected date range"
                  : "No imbalanced posting groups (full history scan)"
              }
              style={{ marginBottom: 16 }}
            />
          ) : imbalanceCount > 0 ? (
            <Alert
              type="warning"
              showIcon
              icon={<WarningOutlined />}
              message={`${imbalanceCount} imbalanced group(s)`}
              description={`Sum of absolute differences: Rs. ${NumberFormat(
                totalAbsDifference,
                2
              )}`}
              style={{ marginBottom: 16 }}
            />
          ) : null}

          <div className="filters-wrap" style={{ marginBottom: 16 }}>
            <Form layout="inline">
              <Form.Item label="Balance as of">
                <DatePicker
                  value={
                    balanceAsOfPicker
                      ? dayjs(balanceAsOfPicker, "YYYY-MM-DD", true)
                      : null
                  }
                  onChange={(d) =>
                    setBalanceAsOfPicker(d ? d.format("YYYY-MM-DD") : "")
                  }
                  allowClear
                  format={config.date_format ? config.date_format : "DD/MM/YYYY"}
                  disabledDate={(c) => c && c > dayjs().endOf("day")}
                />
              </Form.Item>
              <Form.Item>
                <Checkbox
                  checked={restrictDates}
                  onChange={(e) => setRestrictDates(e.target.checked)}
                >
                  Limit per-document scan to date range
                </Checkbox>
              </Form.Item>
              <Form.Item label="Transaction date range">
                <RangePicker
                  value={rangeValue}
                  onChange={onRangeChange}
                  disabled={!restrictDates}
                  disabledDate={(c) => c && c > dayjs().endOf("day")}
                />
              </Form.Item>
              <Form.Item>
                <Space>
                  <Button
                    type="primary"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={load}
                  >
                    Run check
                  </Button>
                </Space>
              </Form.Item>
            </Form>
            {!restrictDates && (
              <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
                Per-document scan runs on <Text strong>all dates</Text> (recommended). Turn on the
                checkbox to narrow the scan; reconciliation always includes all-time GL totals.
              </Paragraph>
            )}
          </div>

          <Row gutter={[16, 16]}>
            <Col span={24}>
              <Table
                size="small"
                bordered
                loading={loading}
                rowKey={(r, i) =>
                  `${r.Reference_Type ?? ""}-${r.Reference_No ?? ""}-${i}`
                }
                columns={columns}
                dataSource={rows}
                pagination={{ pageSize: 25, showSizeChanger: true }}
                scroll={{ x: true }}
              />
            </Col>
          </Row>
        </div>
      </div>
    </>
  );
}

export default PostingIntegrity;
